// Preferencias de cada usuario en ESTE navegador: los accesos rápidos (funciones fijadas), lo
// abierto recientemente y cómo se nombran los menús («Pago de Clientes» o «Cobros»). Son comodidades: si el navegador no deja
// guardar (ventana privada, almacenamiento bloqueado) todo sigue funcionando con los valores por
// defecto. No son datos del ERP: no viajan al servidor ni a otros equipos.
import { useCallback, useSyncExternalStore } from 'react';

const oyentes = new Set<() => void>();
const avisar = () => oyentes.forEach((o) => o());
const suscribir = (o: () => void) => {
  oyentes.add(o);
  return () => oyentes.delete(o);
};

function leer(clave: string): string | null {
  try {
    return localStorage.getItem(clave);
  } catch {
    return null;
  }
}
function escribir(clave: string, valor: string) {
  try {
    localStorage.setItem(clave, valor);
  } catch {
    /* sin almacenamiento: no se recuerda */
  }
  avisar();
}

/** Valor JSON en caché por texto crudo: useSyncExternalStore exige la MISMA referencia si no cambió. */
const cache = new Map<string, { crudo: string | null; valor: unknown }>();
function leerJson<T>(clave: string, porDefecto: T): T {
  const crudo = leer(clave);
  const c = cache.get(clave);
  if (c && c.crudo === crudo) return c.valor as T;
  let valor: T = porDefecto;
  if (crudo) {
    try {
      valor = JSON.parse(crudo) as T;
    } catch {
      valor = porDefecto;
    }
  }
  cache.set(clave, { crudo, valor });
  return valor;
}

// ---------------------------------------------------------------- nombres del menú

/** true = nombres cortos («Cobros», «Pagos»); false = los de Random («Pago de Clientes»). */
export function useNombresCortos(): [boolean, (v: boolean) => void] {
  const v = useSyncExternalStore(suscribir, () => leer('madeci.menu.nombres') === 'cortos');
  return [
    v,
    useCallback((x: boolean) => escribir('madeci.menu.nombres', x ? 'cortos' : 'random'), []),
  ];
}

// ---------------------------------------------------------------- favoritos

/** Accesos rápidos de un usuario que nunca eligió: lo más usado del día a día. */
export const FAVORITOS_INICIALES = [
  'ventas/nota-nueva',
  'ventas/punto-de-venta',
  'ventas/clientes',
  'stock/consulta',
  'cobros/cobrar-factura',
  'operaciones/preparacion',
];

export function useFavoritos(usuarioId: string | undefined) {
  const clave = `madeci.favoritos.${usuarioId ?? 'anon'}`;
  const lista = useSyncExternalStore(suscribir, () => leerJson(clave, FAVORITOS_INICIALES));
  const alternar = useCallback(
    (ruta: string) => {
      const actual = leerJson(clave, FAVORITOS_INICIALES);
      escribir(
        clave,
        JSON.stringify(
          actual.includes(ruta) ? actual.filter((r) => r !== ruta) : [...actual, ruta],
        ),
      );
    },
    [clave],
  );
  return { favoritos: lista, alternar };
}

// ---------------------------------------------------------------- recientes

export interface Reciente {
  tipo: 'nota' | 'cliente' | 'producto';
  id: string;
  titulo: string;
  /** ISO */
  en: string;
}
const SIN_RECIENTES: Reciente[] = [];
const MAX_RECIENTES = 10;

export function useRecientes(usuarioId: string | undefined, empresaId: string | undefined) {
  const clave = `madeci.recientes.${usuarioId ?? 'anon'}.${empresaId ?? ''}`;
  const lista = useSyncExternalStore(suscribir, () => leerJson(clave, SIN_RECIENTES));
  const anotar = useCallback(
    (r: Omit<Reciente, 'en'>) => {
      const actual = leerJson(clave, SIN_RECIENTES);
      const primero = actual[0];
      // Ya está primero con el mismo título: no se reescribe (evita avisos en cadena).
      if (primero && primero.tipo === r.tipo && primero.id === r.id && primero.titulo === r.titulo)
        return;
      const resto = actual.filter((x) => !(x.tipo === r.tipo && x.id === r.id));
      escribir(
        clave,
        JSON.stringify([{ ...r, en: new Date().toISOString() }, ...resto].slice(0, MAX_RECIENTES)),
      );
    },
    [clave],
  );
  return { recientes: lista, anotar };
}
